import { inject } from '@angular/core';
import { CanMatchFn, Route } from '@angular/router';

import { DeviceUiFeatureId, SETTINGS_ROUTE_PATH_TO_FEATURE_ID } from './device-ui-feature-toggle.models';
import { DeviceUiFeatureToggleService } from './device-ui-feature-toggle.service';

/**
 * Creates a {@link CanMatchFn} guard that prevents a route from matching
 * when the corresponding feature has been disabled by the host application.
 *
 * When the guard blocks a route, the lazy-loaded module/component is never loaded,
 * effectively removing the feature from both the navigation and the DOM.
 *
 * @param featureId The device-ui feature to gate on.
 *
 * @example
 * ```typescript
 * {
 *   path: 'network',
 *   loadComponent: () => import('...').then(m => m.IpConfigurationComponent),
 *   canMatch: [canMatchFeature('settings.ip-configuration')]
 * }
 * ```
 */
export function canMatchFeature(featureId: DeviceUiFeatureId): CanMatchFn {
	return (): boolean => {
		const service = inject(DeviceUiFeatureToggleService);
		return service.isFeatureEnabled(featureId);
	};
}

/**
 * A {@link CanMatchFn} guard that resolves the feature ID from the route's `path` property
 * using the {@link SETTINGS_ROUTE_PATH_TO_FEATURE_ID} map.
 *
 * Allows this single guard to be applied to all settings routes without explicit feature ID arguments.
 * When a route path has no mapping, the route is allowed to match (feature enabled by default).
 */
export const canMatchSettingsFeatureByRoute: CanMatchFn = (route: Route): boolean => {
	const service = inject(DeviceUiFeatureToggleService);
	const featureId = route.path ? SETTINGS_ROUTE_PATH_TO_FEATURE_ID.get(route.path) : undefined;
	return featureId ? service.isFeatureEnabled(featureId) : true;
};
